// Admin → Team & Tokens: everyone in this company, and what they can connect with.
//
// This replaces three role pages (Team & Tokens, Project Managers, Developers)
// that each answered "who are my testers / PMs / developers". None answered "who
// has access to this company at all", which is the question an admin actually
// asks when someone joins, leaves, or needs a different role — and answering it
// meant visiting three tabs and holding the union in your head. The role is a
// filter chip here, so each old tab is one click and nothing is lost.
//
// Tokens live here too, for both runners. They used to be split: an admin issued
// a tester's EXTENSION token from the team page, while the tester issued their own
// EXE agent tokens from their own dashboard — so nobody could see, let alone
// control, who was running an agent.
//
// Scope is the company, always: the list comes from /company/team, which the
// server scopes to the caller's own company. There is no cross-tenant view here
// by design — that was the SuperAdmin page this replaces.

import { useMemo, useState } from "react";
import {
  UserPlus, Trash2, Loader2, WifiOff, Send, Pencil, Copy, Check, KeyRound, ShieldOff,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import DataTable from "@/components/dashboard/DataTable";
import LoadError from "@/components/dashboard/LoadError";
import Modal from "@/components/dashboard/Modal";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { toastError, toastInfo, toastSuccess } from "@/lib/toast";
import { validateEmail, validateFullName } from "@/lib/validation";
import ConfirmWithPassword from "@/components/shared/ConfirmWithPassword";
import TokenDrawer from "@/components/company/TokenDrawer";
import { useApiData } from "@/hooks/useApiData";
import { useAuth } from "@/context/AuthContext";
import {
  listTeam, inviteMember, updateTester, removeTester, resendInvite,
} from "@/api/company";
import { resetMemberTwofa } from "@/api/auth";
import { formatDate } from "@/lib/dates";

// Who an admin may create or promote to. `admin` is deliberately absent: this
// page must not be a way to mint another company owner, which is the one change
// that can't be undone by the person making it.
const ASSIGNABLE = [
  { value: "tester", label: "Tester", hint: "Runs tests and reports bugs" },
  { value: "developer", label: "Developer", hint: "Reads reports and marks fixes" },
  { value: "project_manager", label: "Project Manager", hint: "Creates projects and staffs them" },
];

const ROLE_LABEL = {
  tester: "Tester",
  developer: "Developer",
  project_manager: "Project Manager",
  admin: "Admin",
  superadmin: "SuperAdmin",
};
const ROLE_TONE = {
  tester: "brand",
  developer: "emerald",
  project_manager: "sky",
  admin: "amber",
  superadmin: "rose",
};

function initials(name, email) {
  const s = name || email || "?";
  return s.split(/[\s@.]+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}
const fmtDate = (v) => formatDate(v);

/** What this member can currently connect with — drives the Tokens filter.
 *
 * Classifies by the tokens actually held, for EVERY role. It used to stamp
 * anyone who wasn't a tester as "n/a" on the theory that only testers have
 * tokens, which was wrong twice over: an admin holding a live agent token was
 * hidden from every filter (so a "who can connect?" audit missed them), and a
 * developer with genuinely no tokens could not be found under "No tokens" —
 * the one thing the filter exists for.
 *
 * The seat-audit case the "n/a" rule was guarding is still available, and more
 * precisely: filter role = tester AND tokens = none. */
function tokenStateOf(u) {
  const ext = !!u.has_token;
  const exe = (u.exe_token_count || 0) > 0;
  if (ext && exe) return "both";
  if (ext) return "extension";
  if (exe) return "agent";
  return "none";
}

/** Live | Invited | Deactivated — the three states an admin acts on. */
function statusOf(u) {
  if (u.invite_status === "pending") return "invited";
  return u.is_active ? "active" : "deactivated";
}

function CopyBox({ value }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-white/10 dark:bg-white/5">
      <span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-600 dark:text-slate-300">{value}</span>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(value).then(() => {
            setDone(true);
            setTimeout(() => setDone(false), 2000);
          }).catch(() => {});
        }}
        className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-500/10 dark:text-brand-300"
      >
        {done ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
      </button>
    </div>
  );
}

export default function CompanyUsers() {
  const { user: me } = useAuth();
  // include_inactive: a deactivated member is exactly who an admin comes here to
  // find — leaving them out made the page look like they had already been removed.
  const { data, loading, demo, error, reload } = useApiData(
    () => listTeam({ includeInactive: true }), null,
  );
  // `status` is derived from two columns (invite_status + is_active), so it is
  // materialised onto the row: the table sorts and filters on row fields, and a
  // status that only exists inside a render function can do neither.
  const users = useMemo(
    () => (Array.isArray(data) ? data : []).map((u) => ({
      ...u, status: statusOf(u), token_state: tokenStateOf(u),
    })),
    [data],
  );

  const [invite, setInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "tester" });
  const [busy, setBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);  // { email, accept_url }
  const [inviteError, setInviteError] = useState("");
  // Per-field, like inviteError — the name has its own rule and its own message,
  // so pinning it under the email box would point at the wrong input.
  const [inviteNameError, setInviteNameError] = useState("");
  const [editing, setEditing] = useState(null);
  const [editError, setEditError] = useState("");
  const [editNameError, setEditNameError] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [tokensFor, setTokensFor] = useState(null);
  const [delError, setDelError] = useState("");
  const [confirmReset, setConfirmReset] = useState(null);
  const [resetError, setResetError] = useState("");

  const roleOptions = useMemo(
    () => [...new Set(users.map((u) => u.role).filter(Boolean))].sort()
      .map((value) => ({ value, label: ROLE_LABEL[value] || value })),
    [users],
  );

  async function submitInvite() {
    // A bare `return` used to sit here, and the Send button was disabled by the
    // same test — so an invalid address produced no message, no highlight and no
    // request. The button now stays clickable precisely so pressing it says why.
    const emailError = validateEmail(inviteForm.email);
    if (emailError) {
      setInviteError(emailError);
      return;
    }
    // Optional, but not a free-text field: the same shared rule signup uses, and
    // the one the API now enforces too. Without it a name of pure symbols was
    // accepted here and stored verbatim.
    const nameError = inviteForm.name ? validateFullName(inviteForm.name) : "";
    if (nameError) {
      setInviteNameError(nameError);
      return;
    }
    setInviteError("");
    setInviteNameError("");
    setBusy(true);
    try {
      const res = await inviteMember({
        email: inviteForm.email, name: inviteForm.name || null, role: inviteForm.role,
      });
      const { email } = inviteForm;
      setInvite(false);
      setInviteForm({ email: "", name: "", role: "tester" });
      setInviteError("");
      const sent = !!(res && res.email_sent);
      // Only surface the copyable link when the email didn't go out — otherwise
      // the admin is handed a credential they don't need to touch.
      setInviteResult(sent ? null : { email, accept_url: res && res.accept_url });
      if (sent) toastSuccess(`Invitation sent to ${email}.`);
      else toastInfo("Added, but email isn't configured — copy the link to share.");
      await reload();
    } catch (e) {
      setInvite(false);
      toastError((e && e.message) || "Could not send the invitation.");
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit() {
    const emailError = validateEmail(editing.email);
    if (emailError) {
      setEditError(emailError);
      return;
    }
    const nameError = editing.name ? validateFullName(editing.name) : "";
    if (nameError) {
      setEditNameError(nameError);
      return;
    }
    setEditError("");
    setEditNameError("");
    setBusy(true);
    const emailChanged = editing.email.trim().toLowerCase() !== (editing.originalEmail || "").toLowerCase();
    try {
      const res = await updateTester(editing.id, {
        name: editing.name || null,
        email: editing.email.trim(),
        role: editing.role,
        is_active: editing.is_active,
      });
      setEditing(null);
      // Changing the sign-in address is not a routine save — say what it did,
      // including whether the invitation had to go out again.
      if (emailChanged && res?.invite_resent === true) {
        toastSuccess("Email updated — the invitation was re-sent to the new address.");
      } else if (emailChanged && res?.invite_resent === false) {
        toastInfo("Email updated, but the invitation couldn't be re-sent. Use Resend to try again.");
      } else if (emailChanged) {
        toastSuccess("Email updated. Both the old and new addresses have been notified.");
      } else {
        toastSuccess("Saved.");
      }
      await reload();
    } catch (e) {
      toastError((e && e.message) || "Could not save the changes.");
    } finally {
      setBusy(false);
    }
  }

  async function resend(u) {
    try {
      const res = await resendInvite(u.id);
      const sent = !!(res && res.email_sent);
      setInviteResult(sent ? null : { email: u.email, accept_url: res && res.accept_url });
      if (sent) toastSuccess(`Invitation re-sent to ${u.email}.`);
      else toastInfo("Email isn't configured — copy the link to share.");
    } catch (e) {
      toastError((e && e.message) || "Could not resend the invitation.");
    }
  }

  async function confirmRemove(password) {
    setDelError("");
    try {
      await removeTester(confirmDel.id, password);
      const gone = confirmDel.email;
      setConfirmDel(null);
      toastSuccess(`${gone} was removed.`);
      await reload();
    } catch (e) {
      // Stays open on a wrong password so it can be retyped.
      setDelError((e && e.message) || "Could not remove this user.");
    }
  }

  // Account recovery for someone who lost BOTH their authenticator device and
  // their backup codes — the only way back in, since nothing can read a stored
  // secret or code back out.
  async function confirmResetTwofa(password) {
    setResetError("");
    try {
      await resetMemberTwofa(confirmReset.id, password);
      const who = confirmReset.email;
      setConfirmReset(null);
      toastSuccess(`Two-factor authentication cleared for ${who} — they can sign in with just their password and set it up again.`);
      await reload();
    } catch (e) {
      setResetError((e && e.message) || "Could not reset two-factor authentication.");
    }
  }

  const columns = [
    {
      key: "name", header: "User", sortable: true,
      render: (u) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[0.6875rem] font-bold text-white">
            {initials(u.name, u.email)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
              {u.name || (u.email || "").split("@")[0]}
              {u.id === me?.id && <span className="ml-1.5 text-xs text-slate-400">(you)</span>}
            </p>
            <p className="truncate text-xs text-slate-400">{u.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "role", header: "Role", sortable: true,
      render: (u) => (
        <Badge tone={ROLE_TONE[u.role] || "slate"}>{ROLE_LABEL[u.role] || u.role}</Badge>
      ),
    },
    {
      key: "status", header: "Status", sortable: true,
      render: (u) => {
        if (u.status === "invited") return <Badge tone="amber">Invited</Badge>;
        if (u.status === "deactivated") return <Badge tone="slate">Deactivated</Badge>;
        return <Badge tone="emerald">Active</Badge>;
      },
    },
    {
      // Only testers run tests, so a token column for anyone else would be a
      // blank that reads as "missing" rather than "not applicable".
      key: "has_token", header: "Extension", sortable: true,
      render: (u) => (u.role !== "tester"
        ? <span className="text-xs text-slate-300 dark:text-slate-600">n/a</span>
        : u.has_token
          ? <Badge tone="emerald">Connected</Badge>
          : <span className="text-xs text-slate-400">none</span>),
    },
    {
      key: "exe_token_count", header: "Agent PCs", sortable: true,
      render: (u) => {
        if (u.role !== "tester") {
          return <span className="text-xs text-slate-300 dark:text-slate-600">n/a</span>;
        }
        const n = u.exe_token_count || 0;
        if (!n) return <span className="text-xs text-slate-400">none</span>;
        return <Badge tone="sky">{n} machine{n === 1 ? "" : "s"}</Badge>;
      },
    },
    {
      key: "created_at", header: "Joined", align: "right", sortable: true,
      render: (u) => <span className="text-xs text-slate-400">{fmtDate(u.created_at)}</span>,
    },
    {
      key: "actions", header: "", align: "right",
      render: (u) => {
        // An admin may not act on themselves or on another admin here. The server
        // enforces both; hiding the controls stops the click that would 400.
        const untouchable = u.id === me?.id || u.role === "admin" || u.role === "superadmin";
        if (untouchable) {
          return <span className="text-xs text-slate-300 dark:text-slate-600">—</span>;
        }
        return (
          <div className="flex items-center justify-end gap-1">
            {u.status === "invited" && (
              <button
                onClick={() => resend(u)} title="Resend the invitation"
                aria-label={`Resend the invitation to ${u.email}`}
                className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-brand-50 hover:text-brand-600 dark:hover:bg-brand-500/10"
              >
                <Send size={15} />
              </button>
            )}
            {u.role === "tester" && (
              <button
                onClick={() => setTokensFor(u)} title="Issue or revoke agent tokens"
                className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-brand-50 hover:text-brand-600 dark:hover:bg-brand-500/10"
              >
                <KeyRound size={15} />
              </button>
            )}
            <button
              onClick={() => {
                setEditError("");
                setEditNameError("");
                setEditing({
                  id: u.id, email: u.email, name: u.name || "",
                  // Kept so save() can tell an email EDIT from an untouched field
                  // and only then report the notification/re-invite outcome.
                  originalEmail: u.email,
                  invite_status: u.invite_status,
                  role: u.role, is_active: u.is_active !== false,
                });
              }}
              title="Edit name, email, role or access"
              aria-label={`Edit ${u.email}`}
              className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10"
            >
              <Pencil size={15} />
            </button>
            {/* Only offered when there IS a factor to clear — the endpoint 400s
                otherwise, and an always-on button reads as "this does something". */}
            {u.twofa_enabled && (
              <button
                onClick={() => { setResetError(""); setConfirmReset(u); }}
                title="Reset two-factor authentication (lost phone and backup codes)"
                aria-label={`Reset two-factor authentication for ${u.email}`}
                className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-amber-500/10"
              >
                <ShieldOff size={15} />
              </button>
            )}
            <button
              onClick={() => { setDelError(""); setConfirmDel(u); }} title="Remove from the company"
              aria-label={`Remove ${u.email} from the company`}
              className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-500/10"
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Team & Tokens"
        subtitle="Everyone with access to your company — invite them, change what they can do, and issue the tokens their agents connect with."
        actions={
          <Button size="sm" onClick={() => { setInviteError(""); setInvite(true); }}>
            <UserPlus size={15} /> Invite user
          </Button>
        }
      />

      {demo && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Couldn&apos;t reach the server — showing nothing rather than stale data.
        </div>
      )}

      <LoadError message={error} onRetry={reload} />

      {loading ? (
        <div className="flex h-48 items-center justify-center text-slate-400">
          <Loader2 className="animate-spin" /> <span className="ml-2">Loading users…</span>
        </div>
      ) : error ? null : (
        <DataTable
          columns={columns}
          rows={users}
          searchKeys={["name", "email", "role"]}
          filters={[
            { key: "role", label: "roles", options: roleOptions },
            {
              key: "status", label: "status",
              options: [
                { value: "active", label: "Active" },
                { value: "invited", label: "Invited" },
                { value: "deactivated", label: "Deactivated" },
              ],
            },
            {
              // The useful one is "no tokens": a tester occupying a paid seat who
              // has never actually connected anything.
              key: "token_state", label: "tokens",
              options: [
                { value: "extension", label: "Has extension" },
                { value: "agent", label: "Has agent PC" },
                { value: "both", label: "Has both" },
                { value: "none", label: "No tokens" },
              ],
            },
          ]}
          pageSize={25}
          emptyLabel="No users yet — invite someone to get started."
        />
      )}

      {/* invite ------------------------------------------------------------ */}
      <Modal
        open={invite}
        onClose={() => { if (!busy) setInvite(false); }}
        title="Invite a user"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setInvite(false)} disabled={busy}>
              Cancel
            </Button>
            <Button size="sm" onClick={submitInvite}
                    disabled={busy}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send invitation
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            /* Required marker — the existing `text-rose-500 *` convention. Input
               takes `label` as a node, so no new component or styling is needed. */
            label={<>Email<span className="text-rose-500"> *</span></>}
            aria-required="true"
            type="email" placeholder="person@company.com"
            value={inviteForm.email}
            error={inviteError}
            onChange={(e) => { setInviteForm({ ...inviteForm, email: e.target.value }); setInviteError(""); }}
          />
          <Input
            label="Name (optional)" placeholder="Their name"
            value={inviteForm.name}
            error={inviteNameError}
            onChange={(e) => { setInviteForm({ ...inviteForm, name: e.target.value }); setInviteNameError(""); }}
          />
          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-600 dark:text-slate-300">Role</p>
            <div className="space-y-2">
              {ASSIGNABLE.map((r) => (
                <button
                  key={r.value} type="button"
                  onClick={() => setInviteForm({ ...inviteForm, role: r.value })}
                  className={cn(
                    "flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition",
                    inviteForm.role === r.value
                      ? "border-brand-400 bg-brand-500/5"
                      : "border-slate-200 hover:border-slate-300 dark:border-white/10",
                  )}
                >
                  <span className={cn(
                    "mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border-2",
                    inviteForm.role === r.value ? "border-brand-500" : "border-slate-300 dark:border-white/20",
                  )}>
                    {inviteForm.role === r.value && <span className="h-2 w-2 rounded-full bg-brand-500" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-700 dark:text-slate-200">{r.label}</span>
                    <span className="block text-xs text-slate-400">{r.hint}</span>
                  </span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Only testers use a paid seat. Developers and project managers are free.
            </p>
          </div>
        </div>
      </Modal>

      {/* the invitation link, when email couldn't send --------------------- */}
      <Modal
        open={!!inviteResult}
        onClose={() => setInviteResult(null)}
        title="Share this invitation link"
        footer={
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setInviteResult(null)}>Done</Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
          The invitation for <b>{inviteResult?.email}</b> couldn&apos;t be emailed, so send them
          this link yourself. It expires in 7 days.
        </p>
        <CopyBox value={inviteResult?.accept_url} />
      </Modal>

      {/* edit -------------------------------------------------------------- */}
      <Modal
        open={!!editing}
        onClose={() => { if (!busy) setEditing(null); }}
        title={editing ? `Edit ${editing.originalEmail || editing.email}` : ""}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(null)} disabled={busy}>
              Cancel
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={busy}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : null} Save
            </Button>
          </div>
        }
      >
        {editing && (
          <div className="space-y-4">
            <Input
              label="Name" value={editing.name}
              error={editNameError}
              onChange={(e) => { setEditing({ ...editing, name: e.target.value }); setEditNameError(""); }}
            />
            <div>
              <Input
                label="Email" type="email" value={editing.email}
                error={editError}
                onChange={(e) => { setEditing({ ...editing, email: e.target.value }); setEditError(""); }}
              />
              <p className="mt-1.5 text-xs text-slate-400">
                {editing.invite_status === "pending"
                  ? "This is the address their invitation goes to — changing it re-sends the invite."
                  : "This is how they sign in. Both the old and new addresses are notified."}
              </p>
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium text-slate-600 dark:text-slate-300">Role</p>
              <select
                value={editing.role}
                onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
              >
                {ASSIGNABLE.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-slate-400">
                Changing a role changes what they can open on their next sign-in.
              </p>
            </div>
            <label className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox" checked={editing.is_active}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-slate-300"
              />
              <span>
                Active
                <span className="block text-xs text-slate-400">
                  Turning this off keeps their history but blocks them from signing in.
                </span>
              </span>
            </label>
          </div>
        )}
      </Modal>

      <TokenDrawer
        member={tokensFor}
        onClose={() => setTokensFor(null)}
        onChanged={reload}
      />

      <ConfirmWithPassword
        open={!!confirmDel}
        title="Remove this user?"
        confirmLabel="Remove user"
        error={delError}
        onConfirm={confirmRemove}
        onClose={() => { setConfirmDel(null); setDelError(""); }}
      >
        {confirmDel && (
          <>
            <b>{confirmDel.email}</b> will lose access immediately and their invitation
            link, if any, stops working. Their test history stays.
          </>
        )}
      </ConfirmWithPassword>

      <ConfirmWithPassword
        open={!!confirmReset}
        title="Reset two-factor authentication?"
        confirmLabel="Reset 2FA"
        error={resetError}
        onConfirm={confirmResetTwofa}
        onClose={() => { setConfirmReset(null); setResetError(""); }}
      >
        {confirmReset && (
          <>
            <b>{confirmReset.email}</b> will be able to sign in with just their
            password until they set two-factor authentication up again. Their
            authenticator entry and every backup code stop working.
            <br />
            <br />
            Only do this once you are confident it really is them asking — this is
            the one step that removes their second factor.
          </>
        )}
      </ConfirmWithPassword>
    </>
  );
}
